import * as z from 'zod'

import { fetchSiteOperatorGetApiByEmail } from '@/common/api/modules/siteOperator'
import { CreateSiteOperatorVars, UpdateSiteOperatorVars } from '@/common/api/params/siteOperator'
import { NameLabelMapping } from '@/common/app/message'
import {
  CODE_UNIQUE_MESSAGE,
  PASSWORD_MASKING,
  StringLength,
  makeValidateCodeUnique,
  validateCustomerPassword
} from '@/common/form/schema'
import { SiteOperator } from '@/common/model/siteOperator'

export const siteOperatorSchema = z
  .object({
    id: z.number().readonly().optional(),
    siteId: z.number(),
    email: z.email().max(StringLength.NORMAL_MAX),
    password: validateCustomerPassword().or(z.literal(PASSWORD_MASKING)),
    name: z.string().min(1).max(StringLength.NORMAL_MAX),
    permissionTemplateId: z.number().nullable()
  })
  .refine((data) => data.permissionTemplateId !== null, {
    message: '権限テンプレートを選択してください',
    path: ['permissionTemplateId']
  })
  .refine(
    async ({ id, siteId, email }) => {
      const validateUnique = makeValidateCodeUnique(async (email: string) => {
        const data = await fetchSiteOperatorGetApiByEmail(siteId, email)
        return data ? data.id : undefined
      })
      return await validateUnique(id, email)
    },
    {
      message: CODE_UNIQUE_MESSAGE,
      path: ['email']
    }
  )

export type SiteOperatorFormSchemaType = z.infer<typeof siteOperatorSchema>

export const convertSiteOperatorSchemaToVars = (
  siteId: number,
  schema: SiteOperatorFormSchemaType
): CreateSiteOperatorVars | UpdateSiteOperatorVars => {
  return {
    operator_id: schema.id,
    site_id: siteId,
    email: schema.email,
    password: schema.password ?? '',
    name: schema.name,
    permission_template_id: schema.permissionTemplateId ?? null,
    ...(schema.password && schema.password !== PASSWORD_MASKING && { password: schema.password })
  }
}

export const convertSiteOperatorToSchema = (
  operator: SiteOperator
): SiteOperatorFormSchemaType => ({
  id: operator.id,
  siteId: operator.site_id,
  email: operator.email,
  password: PASSWORD_MASKING,
  name: operator.name,
  permissionTemplateId: operator.permission_template_id ?? null
})

export const DEFAULT_SITE_OPERATOR_SCHEMA: Omit<SiteOperatorFormSchemaType, 'siteId'> = {
  email: '',
  password: '',
  name: '',
  permissionTemplateId: null
}

export const siteOperatorNameLabelMapping: NameLabelMapping = {
  email: 'メールアドレス',
  password: 'パスワード',
  name: '名前',
  permissionTemplateId: '権限テンプレート'
}
